import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  deleteIncentiveSlabSet,
  getIncentiveOverview,
  getMemberIncentiveMonth,
  listIncentiveSlabSets,
  saveIncentiveSlabSet,
} from "../../../services/incentiveApi";
import { saveAdminLeadMember } from "../../../services/leadTrackerApi";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const travellers = (value) => Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (period) => {
  if (!period) return "";
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};

const blankSlabSet = () => ({
  name: "",
  memberId: "",
  effectiveFrom: `${currentPeriod()}-01`,
  slabs: [
    { fromCount: 1, toCount: 50, ratePerTraveller: "50" },
    { fromCount: 51, toCount: "", ratePerTraveller: "100" },
  ],
});

function TargetProgress({ counted, target }) {
  if (target == null || Number(target) <= 0) return null;
  const done = Number(counted || 0);
  const percent = Math.min(100, Math.round((done / Number(target)) * 100));
  const met = done >= Number(target);
  return (
    <div className="small text-muted mt-1">
      <div className="progress" style={{ height: 4 }}>
        <div
          className={`progress-bar ${met ? "bg-success" : ""}`}
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {met ? "Target met" : `${percent}% · ${travellers(Number(target) - done)} to go`}
    </div>
  );
}

export default function IncentivesManager() {
  const [period, setPeriod] = useState(currentPeriod);
  const [overview, setOverview] = useState(null);
  const [slabSets, setSlabSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [slabModal, setSlabModal] = useState(null);
  const [targetDrafts, setTargetDrafts] = useState({});
  const [savingTargetId, setSavingTargetId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, slabRows] = await Promise.all([
        getIncentiveOverview(period),
        listIncentiveSlabSets(),
      ]);
      setOverview(overviewData);
      setSlabSets(slabRows);
      setTargetDrafts(Object.fromEntries((overviewData?.members || [])
        .map((row) => [row.memberId, row.travellerTarget == null ? "" : String(row.travellerTarget)])));
    } catch (error) {
      toast.error(errorMessage(error, "Could not load incentives"));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openMember = async (row) => {
    try {
      setDetail(await getMemberIncentiveMonth(row.memberId, period));
    } catch (error) {
      toast.error(errorMessage(error, "Could not load the breakdown"));
    }
  };

  const saveTarget = async (row) => {
    const raw = targetDrafts[row.memberId];
    setSavingTargetId(row.memberId);
    try {
      await saveAdminLeadMember({
        id: row.memberId,
        monthlyTravellerTarget: raw === "" || raw == null ? 0 : Number(raw),
      });
      await load();
      toast.success(`Target saved for ${row.memberName}`);
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the target"));
    } finally {
      setSavingTargetId(null);
    }
  };

  const saveSlabs = async () => {
    const value = slabModal;
    setSaving(true);
    try {
      await saveIncentiveSlabSet({
        ...value,
        memberId: value.memberId ? Number(value.memberId) : null,
        slabs: value.slabs.map((slab) => ({
          fromCount: Number(slab.fromCount),
          toCount: slab.toCount === "" || slab.toCount == null ? null : Number(slab.toCount),
          ratePerTraveller: Number(slab.ratePerTraveller),
        })),
      });
      setSlabModal(null);
      await load();
      toast.success("Slab set saved");
    } catch (error) {
      toast.error(errorMessage(error, "Could not save the slab set"));
    } finally {
      setSaving(false);
    }
  };

  const removeSlabSet = async (row) => {
    if (!window.confirm(`Delete the slab set "${row.name}"?`)) return;
    try {
      await deleteIncentiveSlabSet(row.id);
      await load();
      toast.success("Slab set deleted");
    } catch (error) {
      toast.error(errorMessage(error, "Could not delete the slab set"));
    }
  };

  const patchSlab = (index, field, value) => setSlabModal((old) => ({
    ...old,
    slabs: old.slabs.map((slab, position) => position === index ? { ...slab, [field]: value } : slab),
  }));

  const addSlab = () => setSlabModal((old) => {
    const last = old.slabs[old.slabs.length - 1];
    const nextFrom = Number(last?.toCount || last?.fromCount || 0) + 1;
    return {
      ...old,
      slabs: [...old.slabs.slice(0, -1),
        { ...last, toCount: last?.toCount || nextFrom - 1 },
        { fromCount: nextFrom, toCount: "", ratePerTraveller: "" }],
    };
  });

  const removeSlab = (index) => setSlabModal((old) => ({
    ...old,
    slabs: old.slabs.filter((_, position) => position !== index),
  }));

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">Incentives</div>
          <div className="small text-muted">
            Progressive slabs on Dynamic Pricing chargeable persons, reset every month. Shared bookings count
            only each member's share.
          </div>
        </div>
        <Form.Control
          size="sm"
          type="month"
          style={{ maxWidth: 190 }}
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
        />
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="text-muted"><Spinner size="sm" className="me-2" />Loading incentives...</div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <div className="border rounded px-3 py-2">
                <div className="small text-muted">Net for {monthLabel(overview?.period)}</div>
                <div className="fs-5 fw-semibold">{money(overview?.net)}</div>
              </div>
              <div className="border rounded px-3 py-2">
                <div className="small text-muted">Earned</div>
                <div className="fw-semibold">{money(overview?.earned)}</div>
              </div>
              <div className="border rounded px-3 py-2">
                <div className="small text-muted">Adjustments</div>
                <div className={`fw-semibold ${Number(overview?.adjustments || 0) < 0 ? "text-danger" : ""}`}>
                  {money(overview?.adjustments)}
                </div>
              </div>
              {(overview?.teams || []).map((team) => (
                <div className="border rounded px-3 py-2" key={team.team}>
                  <div className="small text-muted">{team.team} team · {team.members} members</div>
                  <div className="fw-semibold">{money(team.net)}</div>
                  {team.travellerTarget != null && (
                    <div className="small text-muted">
                      {travellers(team.travellersCounted)} of {team.travellerTarget} chargeable persons
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="table-responsive mb-4">
              <Table size="sm" hover bordered className="small mb-0 align-middle bg-white">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Member</th>
                    <th>Team</th>
                    <th className="text-end">Chargeable Persons</th>
                    <th style={{ width: 200 }}>Monthly target</th>
                    <th className="text-end">Rate now</th>
                    <th className="text-end">Earned</th>
                    <th className="text-end">Adjustments</th>
                    <th className="text-end">Net</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(overview?.members || []).map((row, index) => (
                    <tr key={row.memberId}>
                      <td className="fw-semibold">{index + 1}</td>
                      <td className="fw-semibold">{row.memberName}</td>
                      <td>{row.team || "-"}</td>
                      <td className="text-end">{travellers(row.travellersCounted)}</td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control
                            size="sm"
                            type="number"
                            min="0"
                            style={{ maxWidth: 90 }}
                            placeholder="None"
                            value={targetDrafts[row.memberId] ?? ""}
                            onChange={(e) => setTargetDrafts((old) => ({ ...old, [row.memberId]: e.target.value }))}
                          />
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={savingTargetId === row.memberId}
                            onClick={() => saveTarget(row)}
                          >
                            Save
                          </Button>
                        </div>
                        <TargetProgress counted={row.travellersCounted} target={row.travellerTarget} />
                      </td>
                      <td className="text-end">{money(row.currentRate)}</td>
                      <td className="text-end">{money(row.earned)}</td>
                      <td className={`text-end ${Number(row.adjustments || 0) < 0 ? "text-danger" : ""}`}>
                        {money(row.adjustments)}
                      </td>
                      <td className="text-end fw-semibold">{money(row.net)}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-primary" onClick={() => openMember(row)}>Breakdown</Button>
                      </td>
                    </tr>
                  ))}
                  {!overview?.members?.length && (
                    <tr><td colSpan={10} className="text-center text-muted">No team members found.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <div>
                <div className="fw-semibold">Slab Sets</div>
                <div className="small text-muted">
                  A set with no member is the company default. A set for a member overrides it.
                  Rates apply from their effective month onward, so past months keep what was paid.
                </div>
              </div>
              <Button size="sm" variant="outline-secondary" onClick={() => setSlabModal(blankSlabSet())}>+ Slab Set</Button>
            </div>

            <div className="table-responsive">
              <Table size="sm" hover bordered className="small mb-0 align-middle bg-white">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Applies to</th>
                    <th>Effective from</th>
                    <th>Slabs</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {slabSets.map((row) => (
                    <tr key={row.id}>
                      <td className="fw-semibold">{row.name}</td>
                      <td>{row.isDefault ? <Badge bg="secondary">Everyone</Badge> : row.memberName}</td>
                      <td>{monthLabel(String(row.effectiveFrom || "").slice(0, 7))}</td>
                      <td>
                        {(row.slabs || []).map((slab) => (
                          <div key={slab.id}>
                            {slab.fromCount}–{slab.toCount ?? "above"} · {money(slab.ratePerTraveller)} each
                          </div>
                        ))}
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => setSlabModal({
                              ...row,
                              memberId: row.memberId || "",
                              effectiveFrom: row.effectiveFrom,
                              slabs: (row.slabs || []).map((slab) => ({
                                fromCount: slab.fromCount,
                                toCount: slab.toCount ?? "",
                                ratePerTraveller: String(slab.ratePerTraveller ?? ""),
                              })),
                            })}
                          >
                            Edit
                          </Button>
                          <Button size="sm" variant="outline-danger" onClick={() => removeSlabSet(row)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!slabSets.length && (
                    <tr><td colSpan={5} className="text-center text-muted">No slab sets configured.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Card.Body>

      <Modal show={!!detail} onHide={() => setDetail(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            {detail?.memberName} · {monthLabel(detail?.period)}
            {detail?.locked && <Badge bg="secondary" className="ms-2 fw-normal">Closed month</Badge>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <Table size="sm" bordered className="small mb-0 align-middle">
              <thead>
                <tr>
                  <th>Booking</th><th>Trip</th>
                  <th className="text-end">Chargeable Persons</th>
                  <th className="text-end">Share</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(detail.lines || []).map((line) => (
                  <tr key={line.bookingId}>
                    <td>#{line.bookingId}</td>
                    <td>{line.tripTitle || "-"}</td>
                    <td className="text-end">{line.travellers ?? "-"}</td>
                    <td className="text-end">{travellers(line.travellerShare)}</td>
                    <td className="text-end fw-semibold">{money(line.amount)}</td>
                  </tr>
                ))}
                {(detail.adjustmentLines || []).map((line) => (
                  <tr key={`adj-${line.bookingId}`} className="table-light">
                    <td>#{line.bookingId}</td>
                    <td colSpan={3} className="text-muted">
                      Cancelled after {monthLabel(line.originPeriod)} was paid
                    </td>
                    <td className="text-end fw-semibold text-danger">{money(line.amount)}</td>
                  </tr>
                ))}
                {!detail.lines?.length && !detail.adjustmentLines?.length && (
                  <tr><td colSpan={5} className="text-center text-muted">Nothing counted this month.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!slabModal} onHide={() => !saving && setSlabModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">{slabModal?.id ? "Edit Slab Set" : "New Slab Set"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {slabModal && (
            <div className="row g-2">
              <div className="col-12">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  value={slabModal.name}
                  placeholder="e.g. Default, or Avinash 2026"
                  onChange={(e) => setSlabModal((old) => ({ ...old, name: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <Form.Label>Applies to</Form.Label>
                <Form.Select
                  value={slabModal.memberId ?? ""}
                  onChange={(e) => setSlabModal((old) => ({ ...old, memberId: e.target.value }))}
                >
                  <option value="">Everyone (default)</option>
                  {(overview?.members || []).map((row) => (
                    <option key={row.memberId} value={row.memberId}>{row.memberName}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-6">
                <Form.Label>Effective from *</Form.Label>
                <Form.Control
                  type="month"
                  value={String(slabModal.effectiveFrom || "").slice(0, 7)}
                  onChange={(e) => setSlabModal((old) => ({ ...old, effectiveFrom: `${e.target.value}-01` }))}
                />
              </div>
              <div className="col-12">
                <Form.Label className="mb-1">Slabs</Form.Label>
                <div className="small text-muted mb-2">
                  Bands must join up with no gaps, and the last one is left open ended.
                </div>
                {slabModal.slabs.map((slab, index) => (
                  <div className="d-flex gap-2 align-items-center mb-2" key={index}>
                    <Form.Control
                      type="number" min="1" style={{ maxWidth: 90 }} value={slab.fromCount}
                      onChange={(e) => patchSlab(index, "fromCount", e.target.value)}
                    />
                    <span className="small text-muted">to</span>
                    <Form.Control
                      type="number" min="1" style={{ maxWidth: 90 }} value={slab.toCount ?? ""}
                      placeholder="above"
                      onChange={(e) => patchSlab(index, "toCount", e.target.value)}
                    />
                    <span className="small text-muted">at ₹</span>
                    <Form.Control
                      type="number" min="0" step="0.01" style={{ maxWidth: 110 }} value={slab.ratePerTraveller}
                      onChange={(e) => patchSlab(index, "ratePerTraveller", e.target.value)}
                    />
                    <span className="small text-muted">each</span>
                    {slabModal.slabs.length > 1 && (
                      <Button size="sm" variant="outline-danger" onClick={() => removeSlab(index)}>×</Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline-secondary" onClick={addSlab}>+ Band</Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={saving} onClick={() => setSlabModal(null)}>Cancel</Button>
          <Button disabled={saving} onClick={saveSlabs}>{saving ? "Saving..." : "Save Slab Set"}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
